/**
 * ActivityFilters — pill-row of facet filters above the EvaluationActivity
 * audit feed. Three multi-select dropdowns (entity type, action, user) and a
 * 4-segment date-range control (7d / 30d / 90d / All). Selection state is
 * owned by the parent — this component is purely presentational + wires the
 * dropdown open/close locally.
 *
 * Mobile: pills wrap; the row is also inside an overflow-x-auto strip in
 * the parent so very wide filter sets stay scrollable.
 *
 * Linear: MYD-228 (J2).
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import {
	Filter as FilterIcon,
	ChevronDown,
	X as ClearIcon,
	Calendar,
} from 'lucide-react'
import { cn } from 'lib/utils'
import type { AuditFacets, AuditFilters } from 'slices/auditLogSlice'

interface ActivityFiltersProps {
	facets: AuditFacets
	filters: AuditFilters
	onChange: (next: AuditFilters) => void
}

const DATE_RANGES: { key: '7d' | '30d' | '90d' | 'all'; label: string; days: number | null }[] = [
	{ key: '7d', label: '7d', days: 7 },
	{ key: '30d', label: '30d', days: 30 },
	{ key: '90d', label: '90d', days: 90 },
	{ key: 'all', label: 'All', days: null },
]

function rangeKeyForSince(since: string | null | undefined): '7d' | '30d' | '90d' | 'all' {
	if (!since) return 'all'
	try {
		const sinceMs = new Date(since).getTime()
		if (Number.isNaN(sinceMs)) return 'all'
		const diffDays = Math.round((Date.now() - sinceMs) / (1000 * 60 * 60 * 24))
		if (diffDays <= 8) return '7d'
		if (diffDays <= 31) return '30d'
		if (diffDays <= 91) return '90d'
		return 'all'
	} catch {
		return 'all'
	}
}

const ActivityFilters = ({ facets, filters, onChange }: ActivityFiltersProps) => {
	const activeRangeKey = rangeKeyForSince(filters.since)

	const setRange = useCallback(
		(key: '7d' | '30d' | '90d' | 'all') => {
			const def = DATE_RANGES.find((r) => r.key === key)
			if (!def || def.days == null) {
				onChange({ ...filters, since: null })
				return
			}
			const since = new Date(Date.now() - def.days * 24 * 60 * 60 * 1000).toISOString()
			onChange({ ...filters, since })
		},
		[filters, onChange],
	)

	const toggleListValue = useCallback(
		(key: 'entityType' | 'action' | 'userId', value: string) => {
			const current = (filters[key] as string[] | undefined) || []
			const next = current.includes(value)
				? current.filter((v) => v !== value)
				: [...current, value]
			onChange({ ...filters, [key]: next })
		},
		[filters, onChange],
	)

	const clearAll = useCallback(() => {
		onChange({})
	}, [onChange])

	const hasAnyFilter =
		(filters.entityType?.length ?? 0) > 0 ||
		(filters.action?.length ?? 0) > 0 ||
		(filters.userId?.length ?? 0) > 0 ||
		!!filters.since

	const userLabel = (id: string) => {
		const u = facets.users.find((x) => x._id === id)
		return u?.name || u?.email || id
	}

	return (
		<div className="flex flex-wrap items-center gap-2 -mx-1 px-1 overflow-x-auto">
			<span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-gray-500 font-semibold pr-1">
				<FilterIcon className="w-3 h-3" />
				Filter
			</span>

			<MultiSelect
				label="Entity"
				icon={null}
				options={facets.entityTypes}
				selected={filters.entityType ?? []}
				onToggle={(v) => toggleListValue('entityType', v)}
			/>

			<MultiSelect
				label="Action"
				icon={null}
				options={facets.actions}
				selected={filters.action ?? []}
				onToggle={(v) => toggleListValue('action', v)}
			/>

			<MultiSelect
				label="User"
				icon={null}
				options={facets.users.map((u) => u._id)}
				selected={filters.userId ?? []}
				onToggle={(v) => toggleListValue('userId', v)}
				renderOption={userLabel}
			/>

			{/* Date-range segmented control */}
			<div className="inline-flex items-center rounded-md border border-gray-200 overflow-hidden">
				<span className="px-2 py-1.5 text-[11px] uppercase tracking-wide text-gray-500 font-semibold border-r border-gray-200 bg-gray-50">
					<Calendar className="w-3 h-3 inline mr-1" />
					Range
				</span>
				{DATE_RANGES.map((r) => (
					<button
						key={r.key}
						type="button"
						onClick={() => setRange(r.key)}
						className={cn(
							'px-2.5 py-1.5 text-xs font-medium border-l border-gray-200 first:border-l-0 transition-colors',
							activeRangeKey === r.key
								? 'bg-[#0080fe] text-white'
								: 'bg-white text-gray-600 hover:text-gray-900',
						)}
					>
						{r.label}
					</button>
				))}
			</div>

			{hasAnyFilter && (
				<button
					type="button"
					onClick={clearAll}
					className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-gray-500 hover:text-red-600 hover:bg-red-50"
				>
					<ClearIcon className="w-3 h-3" />
					Clear
				</button>
			)}
		</div>
	)
}

interface MultiSelectProps {
	label: string
	icon: React.ReactNode
	options: string[]
	selected: string[]
	onToggle: (value: string) => void
	renderOption?: (value: string) => string
}

function MultiSelect({
	label,
	options,
	selected,
	onToggle,
	renderOption,
}: MultiSelectProps) {
	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return
		const handler = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [open])

	const count = selected.length

	return (
		<div ref={containerRef} className="relative">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className={cn(
					'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors',
					count > 0
						? 'bg-blue-50 border-[#0080fe]/40 text-[#0080fe]'
						: 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
				)}
			>
				{label}
				{count > 0 && (
					<span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0080fe] text-white text-[10px] font-semibold">
						{count}
					</span>
				)}
				<ChevronDown className="w-3 h-3" />
			</button>
			{open && (
				<div className="absolute z-30 mt-1 left-0 min-w-[180px] max-h-[260px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-md p-1">
					{options.length === 0 ? (
						<p className="text-xs text-gray-400 px-2 py-2">No values yet.</p>
					) : (
						options.map((value) => {
							const isSelected = selected.includes(value)
							const label = renderOption ? renderOption(value) : value
							return (
								<button
									key={value}
									type="button"
									onClick={() => onToggle(value)}
									className={cn(
										'flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs text-left hover:bg-gray-50',
										isSelected && 'bg-blue-50/40',
									)}
								>
									<span
										className={cn(
											'w-3 h-3 rounded border flex items-center justify-center shrink-0',
											isSelected
												? 'bg-[#0080fe] border-[#0080fe]'
												: 'bg-white border-gray-300',
										)}
									>
										{isSelected && (
											<svg
												className="w-2 h-2 text-white"
												viewBox="0 0 16 16"
												fill="none"
												aria-hidden="true"
											>
												<path
													d="M3 8l3 3 7-7"
													stroke="currentColor"
													strokeWidth="2.5"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										)}
									</span>
									<span className="flex-1 truncate text-gray-800">{label}</span>
								</button>
							)
						})
					)}
				</div>
			)}
		</div>
	)
}

export default ActivityFilters
